import {initializeApp, getApp} from 'firebase/app';
import {
  getAuth,
  fetchSignInMethodsForEmail,
  setPersistence,
  browserLocalPersistence,
} from 'firebase/auth';
import {getFirestore} from 'firebase/firestore';
import type {FirebaseStorage} from 'firebase/storage';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Exported for services that need to bind additional Firebase products to the
// default app (e.g. dataLayerClient's region-scoped Cloud Functions instance).
export {app};

// --- Firebase App Check ---
// App Check attests that calls to Firebase (Identity Toolkit / Auth, Firestore,
// Storage) come from this genuine web app rather than an arbitrary HTTP client
// wielding the public web API key (pentest finding OP-003). It activates only
// when a reCAPTCHA v3 site key is configured, so local/dev builds without the
// key are unaffected. Server-side *enforcement* is toggled in the Firebase
// console once tokens show up in the App Check metrics dashboard.
const appCheckSiteKey = process.env.REACT_APP_FIREBASE_APPCHECK_SITE_KEY;
if (appCheckSiteKey) {
  const debugToken = process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN;
  if (process.env.NODE_ENV !== 'production' && debugToken) {
    // Register a debug token in Firebase console → App Check → Manage debug
    // tokens. "true" tells the SDK to print an auto-generated token to log.
    (window as any).FIREBASE_APPCHECK_DEBUG_TOKEN =
      debugToken === 'true' ? true : debugToken;
  }
  import('firebase/app-check')
    .then(({initializeAppCheck, ReCaptchaV3Provider}) => {
      initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(appCheckSiteKey),
        isTokenAutoRefreshEnabled: true,
      });
    })
    .catch(err => {
      console.warn('Failed to initialize Firebase App Check:', err);
    });
}

// Get Auth and Firestore instances
export const auth = getAuth(app);
export const db = getFirestore(app);

// Explicitly persist the signed-in user in localStorage so sessions survive
// browser restarts and tab closes. This is the Firebase default in browsers,
// but setting it explicitly guards against future regressions and any
// environment where the default might differ.
setPersistence(auth, browserLocalPersistence).catch(err => {
  console.warn('Failed to set Firebase Auth persistence:', err);
});

/**
 * Returns a Firebase Auth instance whose authDomain points at the default
 * {projectId}.firebaseapp.com endpoint. signInWithPopup opens a popup to
 * https://{authDomain}/__/auth/handler — when authDomain is a custom domain
 * (e.g. app.opalitehealth.com) the hosting catch-all rewrite serves the
 * React app instead of the Firebase handler, breaking the OAuth flow.
 * Using the default domain avoids this entirely.
 */
export function getPopupAuth() {
  try {
    return getAuth(getApp('oauth-popup'));
  } catch {
    const popupApp = initializeApp(
      {
        ...firebaseConfig,
        authDomain: `${firebaseConfig.projectId}.firebaseapp.com`,
      },
      'oauth-popup',
    );
    return getAuth(popupApp);
  }
}

// Lazy-initialize Storage to avoid startup crash when the storage
// service isn't immediately available (e.g. @firebase/* version conflicts).
let storageInstance: FirebaseStorage | null = null;
let storagePromise: Promise<FirebaseStorage> | null = null;
export async function getFirebaseStorage(): Promise<FirebaseStorage> {
  if (!storageInstance) {
    if (!storagePromise) {
      storagePromise = import('firebase/storage').then(({getStorage}) =>
        getStorage(app),
      );
    }
    storageInstance = await storagePromise;
  }
  return storageInstance;
}

// Export auth utilities
export {fetchSignInMethodsForEmail};
